import { useEffect, useRef, useState } from "react";
import { getTransferencias } from "../../api/transferencias.api";

const SPRINTS_OPCOES = [4, 8, 12, 24];

function fmtDias(d) {
  if (d == null) return "—";
  if (d < 1) return `${Math.round(d * 24)}h`;
  return `${Number(d).toFixed(1)}d`;
}

/* "20260601" → "06/01"  ·  outros formatos retornam como vieram */
function sprintLabel(raw) {
  const str = String(raw);
  if (/^\d{8}$/.test(str)) return `${str.slice(4, 6)}/${str.slice(6, 8)}`;
  return str;
}

function esperaColor(dias, media) {
  if (dias == null) return "#475569";
  if (!media) return "#94a3b8";
  if (dias > media * 2) return "#f87171";
  if (dias > media) return "#fbbf24";
  return "#94a3b8";
}

/* ══════════════════════════════════════
   Page
══════════════════════════════════════ */
export default function Transferencias() {
  const [data,    setData]    = useState(null);
  const [sprints, setSprints] = useState(4);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(false);
  const [sortBy,  setSortBy]  = useState("qtd");
  const [sortDir, setSortDir] = useState("desc");
  const loadRef = useRef(null);

  async function load(qtd = sprints) {
    setLoading(true);
    setError(false);
    try {
      const res = await getTransferencias(qtd);
      setData(res);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }

  loadRef.current = () => load(sprints);

  useEffect(() => { load(sprints); }, [sprints]); // eslint-disable-line

  function toggleSort(col) {
    if (sortBy === col) {
      setSortDir(d => (d === "desc" ? "asc" : "desc"));
    } else {
      setSortBy(col);
      setSortDir("desc");
    }
  }

  if (loading) return (
    <div style={page}>
      <div style={{ color: "#475569", textAlign: "center", paddingTop: 60, fontSize: 13 }}>
        Calculando transferências entre sprints…
      </div>
    </div>
  );

  if (error) return (
    <div style={page}>
      <div style={{ color: "#f87171", textAlign: "center", paddingTop: 60, fontSize: 14 }}>
        Erro ao carregar dados.{" "}
        <button onClick={() => loadRef.current()} style={{ color: "#60a5fa", background: "none", border: "none", cursor: "pointer", fontSize: 14 }}>
          Tentar novamente
        </button>
      </div>
    </div>
  );

  const tickets = data?.tickets || [];
  const resumo  = data?.resumo  || {};
  const fluxo   = data?.fluxo_sprints || [];

  const esperas    = tickets.map(t => t.tempo_aguardando_dias).filter(d => d != null);
  const mediaEspera = esperas.length ? esperas.reduce((s, d) => s + d, 0) / esperas.length : null;

  const ticketsSorted = [...tickets].sort((a, b) => {
    let va, vb;
    if (sortBy === "espera") {
      va = a.tempo_aguardando_dias;
      vb = b.tempo_aguardando_dias;
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
    } else {
      va = a.qtd_transferencias;
      vb = b.qtd_transferencias;
    }
    return sortDir === "desc" ? vb - va : va - vb;
  });

  return (
    <div style={page}>

      {/* ── Header ── */}
      <header style={header}>
        <div>
          <h1 style={h1}>Transferências entre Sprints</h1>
          <div style={{ fontSize: 12, color: "#475569", marginTop: 3 }}>
            Tickets empurrados de sprint para sprint · {data?.periodo
              ? `${data.periodo.sprints} sprints (${data.periodo.versoes.map(sprintLabel).join(", ")})`
              : `últimas ${sprints} sprints`}
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 }}>
            <span style={{ fontSize: 9, fontWeight: 700, color: "#334155", textTransform: "uppercase", letterSpacing: "0.07em" }}>
              Período de análise
            </span>
            <div style={{ display: "flex", gap: 4 }}>
              {SPRINTS_OPCOES.map(n => (
                <button
                  key={n}
                  onClick={() => setSprints(n)}
                  style={{
                    padding: "5px 12px", borderRadius: 6, cursor: "pointer",
                    fontSize: 11, fontWeight: 500, transition: "all 0.15s",
                    background: sprints === n ? "rgba(96,165,250,0.2)" : "rgba(255,255,255,0.04)",
                    border: sprints === n ? "1px solid rgba(96,165,250,0.4)" : "1px solid rgba(255,255,255,0.1)",
                    color: sprints === n ? "#60a5fa" : "rgba(255,255,255,0.4)",
                  }}
                >
                  {n} sprints
                </button>
              ))}
            </div>
          </div>
          <button onClick={() => loadRef.current()} style={btnRefresh}>↻</button>
        </div>
      </header>

      {/* ── KPI Cards ── */}
      <div style={kpiRow}>
        <div style={{ ...kpiCard, borderColor: "rgba(96,165,250,0.2)" }}>
          <div style={kpiLabel}>Tickets Transferidos</div>
          <div style={{ fontSize: 30, fontWeight: 900, color: "#60a5fa", lineHeight: 1 }}>
            {resumo.tickets_transferidos ?? 0}
          </div>
        </div>
        <div style={{ ...kpiCard, borderColor: "rgba(167,139,250,0.2)" }}>
          <div style={kpiLabel}>Total de Transferências</div>
          <div style={{ fontSize: 30, fontWeight: 900, color: "#a78bfa", lineHeight: 1 }}>
            {resumo.total_transferencias ?? 0}
          </div>
        </div>
        <div style={{ ...kpiCard, borderColor: "rgba(251,191,36,0.2)" }}>
          <div style={kpiLabel}>Ticket Recordista</div>
          {resumo.max_transferencias ? (
            <>
              <a
                href={`https://redmine.lexsis.com.br/issues/${resumo.max_transferencias.id}`}
                target="_blank" rel="noreferrer"
                style={{ fontSize: 22, fontWeight: 900, color: "#fbbf24", lineHeight: 1, textDecoration: "none" }}
              >
                #{resumo.max_transferencias.id}
              </a>
              <div style={{ fontSize: 11, color: "#475569", marginTop: 4 }}>
                {resumo.max_transferencias.qtd}x transferido
              </div>
            </>
          ) : (
            <div style={{ fontSize: 22, fontWeight: 900, color: "#475569" }}>—</div>
          )}
        </div>
        <div style={{ ...kpiCard, borderColor: "rgba(248,113,113,0.2)" }}>
          <div style={kpiLabel}>Maior Espera</div>
          {resumo.maior_espera_dias ? (
            <>
              <div style={{ fontSize: 30, fontWeight: 900, color: "#f87171", lineHeight: 1 }}>
                {fmtDias(resumo.maior_espera_dias.dias)}
              </div>
              <a
                href={`https://redmine.lexsis.com.br/issues/${resumo.maior_espera_dias.id}`}
                target="_blank" rel="noreferrer"
                style={{ fontSize: 11, color: "#475569", marginTop: 4, textDecoration: "none" }}
              >
                #{resumo.maior_espera_dias.id}
              </a>
            </>
          ) : (
            <div style={{ fontSize: 22, fontWeight: 900, color: "#475569" }}>—</div>
          )}
        </div>
      </div>

      {/* ── Fluxo entre sprints ── */}
      {fluxo.length > 0 && (
        <div style={glass}>
          <div style={sectionLabel}>Fluxo entre Sprints</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {fluxo.map((f, i) => (
              <div key={i} style={fluxoChip}>
                <span style={{ color: "#94a3b8" }}>{sprintLabel(f.de)}</span>
                <span style={{ color: "#334155", margin: "0 6px" }}>→</span>
                <span style={{ color: "#94a3b8" }}>{sprintLabel(f.para)}</span>
                <span style={fluxoQtd}>{f.qtd}x</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Tabela de tickets ── */}
      {ticketsSorted.length === 0 ? (
        <div style={{ color: "#334155", textAlign: "center", paddingTop: 40, fontSize: 13 }}>
          Nenhum ticket transferido no período selecionado.
        </div>
      ) : (
        <div style={glass}>
          <div style={sectionLabel}>Tickets Transferidos</div>
          <div style={{ overflowX: "auto" }}>
            <table style={table}>
              <thead>
                <tr>
                  <th style={th}>Ticket</th>
                  <th style={th}>Status</th>
                  <th style={th}>Dev</th>
                  <th style={th}>Size</th>
                  <th style={th}>Grupo</th>
                  <th style={th}>Histórico de Sprints</th>
                  <th
                    style={{ ...th, textAlign: "right", cursor: "pointer", userSelect: "none" }}
                    onClick={() => toggleSort("qtd")}
                  >
                    Qtd. Transf. {sortBy === "qtd" && (sortDir === "desc" ? "▼" : "▲")}
                  </th>
                  <th style={{ ...th, textAlign: "center" }}>Em Desenvolvimento</th>
                  <th
                    style={{ ...th, textAlign: "right", cursor: "pointer", userSelect: "none" }}
                    onClick={() => toggleSort("espera")}
                  >
                    Tempo Aguardando {sortBy === "espera" && (sortDir === "desc" ? "▼" : "▲")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {ticketsSorted.map(t => (
                  <tr
                    key={t.id}
                    style={{ ...trRow, cursor: "pointer" }}
                    onDoubleClick={() => window.open(`https://redmine.lexsis.com.br/issues/${t.id}`, "_blank")}
                    onMouseEnter={ev => { ev.currentTarget.style.background = "rgba(255,255,255,0.03)"; }}
                    onMouseLeave={ev => { ev.currentTarget.style.background = "transparent"; }}
                  >
                    <td style={td}>
                      <a
                        href={`https://redmine.lexsis.com.br/issues/${t.id}`}
                        target="_blank" rel="noreferrer"
                        onClick={e => e.stopPropagation()}
                        style={{ color: "#60a5fa", textDecoration: "none", fontWeight: 600 }}
                        title={t.subject}
                      >
                        #{t.id}
                      </a>
                      <div style={{ fontSize: 11, color: "#475569", maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {t.subject}
                      </div>
                    </td>
                    <td style={{ ...td, fontSize: 12, color: "#94a3b8" }}>{t.status}</td>
                    <td style={{ ...td, fontSize: 12, color: "#94a3b8" }}>{t.desenvolvedor || "—"}</td>
                    <td style={{ ...td, fontSize: 12, color: "#94a3b8" }}>{t.size || "—"}</td>
                    <td style={td}>
                      {t.grupo_entrega ? <span style={sprintChip}>{t.grupo_entrega}</span> : "—"}
                    </td>
                    <td style={td}>
                      <div style={{ display: "flex", alignItems: "center", gap: 4, flexWrap: "wrap" }}>
                        {t.historico_sprints.map((s, i) => (
                          <span key={i} style={{ display: "flex", alignItems: "center", gap: 4 }}>
                            {i > 0 && <span style={{ color: "#334155", fontSize: 10 }}>→</span>}
                            <span style={histChip}>{sprintLabel(s)}</span>
                          </span>
                        ))}
                      </div>
                    </td>
                    <td style={{ ...td, textAlign: "right", fontWeight: 800, color: "#a78bfa", fontSize: 14 }}>
                      {t.qtd_transferencias}
                    </td>
                    <td style={{ ...td, textAlign: "center" }}>
                      {t.em_desenvolvimento ? (
                        <span style={{ ...badge, color: "#4ade80", background: "rgba(74,222,128,0.1)", border: "1px solid rgba(74,222,128,0.25)" }}>
                          Em Desenvolvimento
                        </span>
                      ) : (
                        <span style={{ ...badge, color: "#fbbf24", background: "rgba(251,191,36,0.1)", border: "1px solid rgba(251,191,36,0.25)" }}>
                          Aguardando
                        </span>
                      )}
                    </td>
                    <td style={{ ...td, textAlign: "right", fontWeight: 700, color: esperaColor(t.tempo_aguardando_dias, mediaEspera) }}>
                      {fmtDias(t.tempo_aguardando_dias)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Styles ── */

const page = { padding: "28px 32px", minHeight: "100vh", color: "#f1f5f9" };

const header = {
  display: "flex", alignItems: "flex-start",
  justifyContent: "space-between", marginBottom: 24, gap: 12, flexWrap: "wrap",
};

const h1 = { fontSize: 22, fontWeight: 800, color: "#f1f5f9", margin: 0, letterSpacing: "-0.01em" };

const btnRefresh = {
  background: "rgba(96,165,250,0.08)", border: "1px solid rgba(96,165,250,0.15)",
  color: "#60a5fa", borderRadius: 8, padding: "6px 14px", cursor: "pointer", fontSize: 16,
};

const kpiRow = {
  display: "grid",
  gridTemplateColumns: "repeat(4, 1fr)",
  gap: 10,
  marginBottom: 16,
};

const kpiCard = {
  background: "rgba(11,22,42,0.72)",
  backdropFilter: "blur(18px)",
  border: "1px solid",
  borderRadius: 12,
  padding: "14px 16px",
  minWidth: 100,
};

const kpiLabel = {
  fontSize: 9, fontWeight: 700, color: "rgba(255,255,255,0.3)",
  textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 6,
};

const glass = {
  background: "rgba(11,22,42,0.72)",
  backdropFilter: "blur(18px)",
  WebkitBackdropFilter: "blur(18px)",
  border: "1px solid rgba(96,165,250,0.1)",
  borderRadius: 14,
  padding: "18px 20px",
  boxShadow: "0 8px 32px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.04)",
  marginBottom: 16,
};

const sectionLabel = {
  fontSize: 11, fontWeight: 700, color: "rgba(255,255,255,0.3)",
  textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 14,
};

const table = { width: "100%", borderCollapse: "collapse", fontSize: 13 };

const th = {
  textAlign: "left", padding: "10px 14px", fontSize: 11, fontWeight: 600,
  color: "#475569", borderBottom: "1px solid rgba(96,165,250,0.08)",
  whiteSpace: "nowrap", letterSpacing: "0.05em", textTransform: "uppercase",
};

const td = {
  padding: "10px 14px",
  borderBottom: "1px solid rgba(255,255,255,0.04)",
  verticalAlign: "middle",
};

const trRow = { transition: "background 0.12s" };

const sprintChip = {
  fontSize: 10,
  color: "#64748b",
  background: "rgba(71,85,105,0.2)",
  padding: "2px 7px",
  borderRadius: 4,
  whiteSpace: "nowrap",
};

const histChip = {
  fontSize: 10,
  color: "#94a3b8",
  background: "rgba(255,255,255,0.04)",
  border: "1px solid rgba(96,165,250,0.1)",
  padding: "2px 6px",
  borderRadius: 4,
  whiteSpace: "nowrap",
};

const fluxoChip = {
  display: "flex", alignItems: "center",
  fontSize: 12, fontWeight: 600,
  background: "rgba(255,255,255,0.03)",
  border: "1px solid rgba(96,165,250,0.1)",
  borderRadius: 8,
  padding: "6px 12px",
};

const fluxoQtd = {
  marginLeft: 8, fontSize: 11, fontWeight: 800, color: "#60a5fa",
  background: "rgba(96,165,250,0.1)", borderRadius: 99, padding: "1px 7px",
};

const badge = {
  fontSize: 10, fontWeight: 700, padding: "3px 10px", borderRadius: 99,
  whiteSpace: "nowrap", display: "inline-block",
};
